import React, { useEffect, useState }                  from 'react'
import { useTranslation }                              from 'react-i18next'
import {
  Box,
  Button, Chip,
  Container, FormControl,
  FormControlLabel, FormGroup,
  Grid, Input, InputLabel, Link, MenuItem,
  Paper, Select, Stack, Switch, Typography,
}                                                      from '@mui/material'
import AlertSimpleDialog                               from '../../components/alert-simple-dialog'
import { useApi }                                      from '../../utils/api'
import { Link as RouterLink, useNavigate, useParams }  from 'react-router-dom'
import { useSnackbar }                                 from 'notistack'
import { dateFromInputStringToISO, dateToInputString } from '../../utils/dateutils'
import { DateTime }                                    from 'luxon'
import ShortFormControl                                from '../../components/short-form-control'
import useProviders                                    from '../../hooks/useProviders'
import { ProviderBillingCodes }                        from './ProviderBillingCodes'
import Consts                                          from '../../consts'
import BillingCodeOverview                             from './BillingCodeOverview'
import { fPrice }                                      from '../../utils/textutils'
import BaseTable                                       from '../../components/base-table'
import Iconify                                         from '../../components/Iconify'

export default function ServicePage() {

  const api = useApi()
  const navigate = useNavigate()
  const { serviceId } = useParams()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()
  const [providers] = useProviders()
  const [partners, setPartners] = useState([])
  const [categories, setCategories] = useState([])
  const [values, setValues] = useState({
    name: '',
    number: '',
    pattern: '',
    keyword: '',
    type: Consts.Service.type_microPayment,
    enabled: false,
    activeFrom: dateToInputString(DateTime.now().toISO()),
    activeTo: dateToInputString(DateTime.now().plus({ month: 1 }).toISO()),
    url: '',
    serviceProvider: '',
    onBehalfOf: '',
    projectId: '',
    taxPercent: 0.23,
    allowedOperators: [],
    verificationEnabled: false,
    autoRecognizeKeyword: false,
    verificationOperators: [],
    verificationSmsText: '',
    partnerId: '',
  })
  const [shownDeleteDialog, setShownDeleteDialog] = useState(false)
  const [forceReloadCounter, setForceReloadCounter] = useState(0)

  useEffect(() => {
    api.partner.list(null, 0, 1000).then(({ data }) => setPartners(data))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    (async () => {
      if (!isNaN(serviceId)) {
        let service = await api.service.detail(serviceId).catch(() => ({}))
        setValues({
          ...values,
          ...service,
          activeFrom: dateToInputString(service.activeFrom),
          activeTo: dateToInputString(service.activeTo),
        })
      }
      let cats = await api.category.list().catch(() => ({}))
      setCategories((prev) => {
        return cats?.data ?? []
      })
    })()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceId])

  const handleCloseDeleteDialog = (result) => {
    setShownDeleteDialog(false)
    if (result) {
      api.service.delete(serviceId).then(() => {
        enqueueSnackbar(t('Service deleted'), {
          variant: 'success'
        })
        navigate('/dashboard/services')
      }).catch(error => {
        enqueueSnackbar(t('Failed to delete service'), {
          variant: 'error'
        })
      })
    }
  }

  const onSubmit = async (e) => {
    e.preventDefault()
    let data = {
      ...values,
      activeFrom: dateFromInputStringToISO(values.activeFrom),
      activeTo: dateFromInputStringToISO(values.activeTo),
      messagesDefinition: {},
      verificationSmsText: values.verificationEnabled ? values.verificationSmsText : '',
      verificationOperators: values.verificationEnabled ? values.verificationOperators : [],
    }
    try {
      let service = await api.service.save(data)
      navigate(`/dashboard/services/${service.id}`)
      enqueueSnackbar(t('Service saved'), {
        variant: 'success'
      })
    } catch (e) {
      enqueueSnackbar(t('Failed to save service'), {
        variant: 'error'
      })
    }
  }

  const handleChange = (prop) => (event) => {
    handleChangeDirect(prop, event.target.value)
  }

  const handleChangeDirect = (prop, value) => {
    setValues({ ...values, [prop]: value });
  }

  const ServiceSharesTable = ({ service }) => {

    const api = useApi()
    const { t } = useTranslation()

    const columns = [
      { field: 'id', headerName: t('ID'), width: 65, sortable: false, filterable: false, },
      { field: 'name', headerName: t('Name'), flex: 1, sortable: true, filterable: false, renderCell: (cell) => {
          return (
            <Link
              component={RouterLink}
              variant="body2"
              to={`/dashboard/services/${cell.row.serviceId}/share/${cell.row.id}`}
            >
              {cell.row.name ?? '---'}
            </Link>
          )
        }
      },
      { field: 'operator', headerName: t('Operator'), flex: 1, sortable: true, valueGetter: (params) => params.row.operator?.name ?? '---', filterable: false },
      { field: 'messageType', headerName: t('Share type'), flex: 1, sortable: true, filterable: false, renderCell: (cell) => {
          return t(`messageType_${cell.row.messageType}`)
        }
      },
      { field: 'position', headerName: t('Share position'), flex: 1, sortable: false, filterable: false },
      { field: 'value', headerName: t('Share value'), flex: 1, sortable: false, filterable: false, renderCell: (cell) => {
          if (cell.row.variant === Consts.Share.variantPrice) {
            return fPrice(cell.row?.value ?? 0, 4)
          }
          return t('share-fm-mt-val-percent', { val: (cell.row?.value ?? 0) * 100 })
        }
      },
      { field: 'mtValue', headerName: t('Share mt value'), flex: 1, sortable: false, filterable: false, renderCell: (cell) => {
          if (cell.row.variant === Consts.Share.variantPrice) {
            return fPrice(cell.row?.mtValue ?? 0, 4)
          }
          return t('share-fm-mt-val-percent', { val: (cell.row?.mtValue ?? 0) * 100 })
        }
      },
    ]

    return <>
      <Grid item xs={12}>
        <Stack direction="row" alignItems="baseline" spacing={2} mt={3} mb={1}>
          <Typography variant="h6" gutterBottom>{t('Service shares')}</Typography>
          <Box flexGrow={'1'}/>
          <Button variant="contained"
                  component={RouterLink}
                  to={`/dashboard/services/${service.id}/share/create`}
                  startIcon={<Iconify icon="material-symbols:add"/>}>
            {t('New service share')}
          </Button>
        </Stack>
        <BaseTable
          columns={columns}
          loadCallback={async (query, page, pageSize) => {
            try {
              return await api.share.list(service?.id, query, page, pageSize)
            } catch (e) {
              console.log('Failed to load service shares', e)
            }
          }}
        />
      </Grid>
    </>
  }

  return (
    <Container component="main">
      <Grid component={'form'} onSubmit={onSubmit} item xs={12}>
        <Paper sx={{ p: 2, width: '100%' }}>
          <Grid container sx={{ width: '100%' }} spacing={2}>
            <Grid item xs={12} md={8}>
              <ShortFormControl
                name={'name'}
                title={t('Name')}
                value={values.name ?? ''}
                handleChange={handleChange} />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormGroup>
                <ShortFormControl
                  name={'projectId'}
                  title={t('Project ID')}
                  value={values.projectId}
                  handleChange={handleChange}
                  inputProps={{
                    type: 'number',
                  }}
                />
              </FormGroup>
            </Grid>
            <Grid item xs={12} md={4}>
              <ShortFormControl
                name={'number'}
                title={t('Tel. number')}
                value={values.number}
                handleChange={handleChange} />
            </Grid>
            <Grid item xs={12} md={6}>
              <ShortFormControl
                name={'pattern'}
                title={t('Pattern')}
                value={values.pattern}
                handleChange={handleChange} />
            </Grid>
            <Grid item xs={12} md={2}>
              <ShortFormControl
                name={'keyword'}
                title={t('Keyword')}
                value={values.keyword}
                handleChange={handleChange} />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormGroup>
                <ShortFormControl
                  name={'activeFrom'}
                  title={t('Active from')}
                  value={values.activeFrom}
                  handleChange={handleChange}
                  inputProps={{
                    type: 'datetime-local',
                  }}
                />
              </FormGroup>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormGroup>
                <ShortFormControl
                  name={'activeTo'}
                  title={t('Active to')}
                  value={values.activeTo}
                  handleChange={handleChange}
                  inputProps={{
                    type: 'datetime-local',
                  }}
                />

              </FormGroup>
            </Grid>
            <Grid item xs={12}>
              <FormGroup>
                <ShortFormControl
                  name={'taxPercent'}
                  title={t('tax_info', { value: (0.23).toLocaleString('sk-SK'), percent: 23 })}
                  value={values.taxPercent ?? ''}
                  handleChange={handleChange}
                  inputProps={{
                    type: 'number',
                    step: 0.01,
                    max: 1,
                    min: 0,
                  }}
                />
              </FormGroup>
            </Grid>
            <Grid item xs={12}>
              <FormGroup>
                <FormControlLabel control={<Switch />} checked={values.enabled} onChange={(e) => {
                  handleChangeDirect('enabled', e.target.checked)
                }} label={t('Enabled')} />
              </FormGroup>
            </Grid>
            <Grid item xs={12}>
              <FormGroup>
                <FormControlLabel control={<Switch />} checked={values.autoRecognizeKeyword} onChange={(e) => {
                  handleChangeDirect('autoRecognizeKeyword', e.target.checked)
                }} label={t('Service recognize keyword automatically')} />
              </FormGroup>
            </Grid>
            <Grid item xs={12}>
              <FormGroup>
                <FormControlLabel control={<Switch />} checked={values.verificationEnabled} onChange={(e) => {
                  handleChangeDirect('verificationEnabled', e.target.checked)
                }} label={t('Service verification enabled')} />
                <FormControl variant="standard" sx={{ mb: 1 }} fullWidth>
                  <InputLabel id="service-verification-operators">{t('Service verification operators')}</InputLabel>
                  <Select
                    labelId="service-verification-operators"
                    id="sel-service-verification-operators"
                    multiple
                    value={values.verificationEnabled ? values.verificationOperators : []}
                    onChange={(e) => {
                      handleChangeDirect('verificationOperators', e.target.value)
                    }}
                    input={<Input id="select-verification-operators" label={t('Service verification operators')} disabled={!values.verificationEnabled} />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={Consts.Operators.find(it => it.id === value)?.name ?? value} />
                        ))}
                      </Box>
                    )}
                  >
                    {Consts.Operators.map((operator) => (
                      <MenuItem
                        key={operator.id}
                        value={operator.id}
                      >
                        {operator.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <ShortFormControl
                  name={'verificationSmsText'}
                  title={t('Service verification SMS text')}
                  value={!values.verificationEnabled ? '' : (values.verificationSmsText ?? '')}
                  handleChange={handleChange}
                  inputProps={{
                    disabled: !values.verificationEnabled,
                  }}
                />
              </FormGroup>
            </Grid>
            <Grid item xs={12}>
              <FormControl variant="standard" fullWidth>
                <InputLabel id="demo-multiple-chip-label">{t('Allowed operators')}</InputLabel>
                <Select
                  labelId="demo-multiple-chip-label"
                  id="demo-multiple-chip"
                  multiple
                  value={values.allowedOperators}
                  onChange={(e) => {
                    handleChangeDirect('allowedOperators', e.target.value)
                  }}
                  input={<Input id="select-multiple-chip" label={t('Allowed operators')} />}
                  renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={Consts.Operators.find(it => it.id === value)?.name ?? value} />
                        ))}
                      </Box>
                    )}
                >
                  {Consts.Operators.map((operator) => (
                    <MenuItem
                      key={operator.id}
                      value={operator.id}
                    >
                      {operator.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl variant="standard" fullWidth>
                <InputLabel id="type-select-label">{t('Type')}</InputLabel>
                <Select
                  labelId="type-select-label"
                  id="type-select"
                  value={values?.type ?? ''}
                  label={t('Type')}
                  onChange={handleChange('type')}
                >
                  <MenuItem key={`type-premium`} value={Consts.Service.type_premium}>{t(Consts.Service.type_premium)}</MenuItem>
                  <MenuItem key={`type-micropayments`} value={Consts.Service.type_microPayment}>{t(Consts.Service.type_microPayment)}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              {partners?.length > 0 && <>
                <FormControl variant="standard" fullWidth>
                  <InputLabel id="partner-select-label">{t('Partner')}</InputLabel>
                  <Select
                    labelId="partner-select-label"
                    id="partner-select"
                    value={values?.partnerId ?? ''}
                    label={t('Partner')}
                    onChange={handleChange('partnerId')}
                  >
                    { partners.map(partner => (<MenuItem key={`partner-${partner.id}`} value={partner.id}>{partner.name}</MenuItem>)) }
                  </Select>
                </FormControl>
              </>}
            </Grid>
            <Grid item xs={12}>
              {categories?.length > 0 && <>
                <FormControl variant="standard" fullWidth>
                  <InputLabel id="category-select-label">{t('Category')}</InputLabel>
                  <Select
                    labelId="category-select-label"
                    id="category-select"
                    value={values?.categoryId ?? ''}
                    label={t('Category')}
                    onChange={handleChange('categoryId')}
                  >
                    { categories.map(cat => (<MenuItem key={`category-${cat.id}`} value={cat.id}>{cat.title}</MenuItem>)) }
                  </Select>
                </FormControl>
              </>}
            </Grid>
            <Grid item xs={12}>
              <FormGroup>
                <ShortFormControl
                  name={'url'}
                  title={t('Url')}
                  value={values.url}
                  handleChange={handleChange}
                />
              </FormGroup>
            </Grid>
            <Grid item xs={12}>
              <FormGroup>
                <ShortFormControl
                  name={'serviceProvider'}
                  title={t('Service provider')}
                  value={values.serviceProvider}
                  handleChange={handleChange}
                />
              </FormGroup>
            </Grid>
            <Grid item xs={12}>
              <FormGroup>
                <ShortFormControl
                  name={'onBehalfOf'}
                  title={t('On behalf of')}
                  value={values.onBehalfOf}
                  handleChange={handleChange}
                />
              </FormGroup>
            </Grid>
          </Grid>
        </Paper>
        <Box sx={{ mt: 2, px: 1 }}>
          <Button type={'submit'} variant={'contained'}>{t('Save service')}</Button>
          { !isNaN(serviceId) && (<Button type={'button'} color={'error'} variant={'contained'} sx={{ ml: 2 }} onClick={() => { setShownDeleteDialog(true) }}>{t('Delete')}</Button>) }
        </Box>
      </Grid>
      <Grid item xs={12}>
        <Box sx={{ mt: 2, px: 1 }}>
          <Typography variant="h4" gutterBottom>{t('Provider billing codes')}</Typography>
        </Box>
        {(providers?.length ?? 0) > 0 && !isNaN(serviceId) && <>
          {providers.filter(pr => pr.type === values?.type).map(provider => {
            return <ProviderBillingCodes key={`provider-bill-codes-${provider.id}`} provider={provider} serviceId={serviceId} onUpdate={() => setForceReloadCounter(prev => prev + 1)} />
          })}
        </>}
        {!isNaN(serviceId) &&
          <Box sx={{ mt: 2 }}>
            <BillingCodeOverview serviceId={serviceId} forceReloadCounter={forceReloadCounter} />
          </Box>
        }
      </Grid>
      {values?.id && <ServiceSharesTable service={values} />}
      <AlertSimpleDialog
        title={t('Deleting service')}
        message={t('delete-confirm-msg', { name: values.name })}
        open={shownDeleteDialog}
        onClose={handleCloseDeleteDialog}
      />
    </Container>
  )

}